import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';
import { DemoDataService } from 'src/shared/services/demo-data.service';

export interface SharedExpense {
  id: number;
  unit_id: number;
  description: string;
  amount: number;
  period: string;  // e.g. "June 2026"
  allocation: { space_name: string; tenant_name: string; share: number }[];
  status: 'allocated' | 'collected' | 'pending';
}

export interface WaitlistEntry {
  id: number;
  unit_id: number;
  name: string;
  email: string;
  desired_move_in: string;
  room_preference: string | null;
  status: 'New' | 'Contacted' | 'Touring' | 'Applied';
  added_at: string;
}

export interface UpkeepTask {
  id: number;
  unit_id: number;
  area: string;   // "Shared kitchen"
  task: string;
  frequency: 'Weekly' | 'Biweekly' | 'Monthly';
  next_due: string;
  assigned_to: string;  // tenant or "Cleaning pro"
  space_id?: number;    // set when the task is scoped to a specific room (vs a common area)
}

const DUMMY_SHARED_EXPENSES: SharedExpense[] = [
  // --- Palmetto House (unit 9) — utilities split across all 4 rooms ---
  {
    id: 1, unit_id: 9, description: 'Electricity · Duke Energy', amount: 186, period: 'July 2026',
    status: 'collected',
    allocation: [
      { space_name: 'Room A', tenant_name: 'Marcus Whitfield', share: 47 },
      { space_name: 'Room B', tenant_name: 'Keisha Boone', share: 47 },
      { space_name: 'Room C', tenant_name: 'Felix Ostrander', share: 46 },
      { space_name: 'Room D', tenant_name: 'Vacant', share: 46 },
    ],
  },
  {
    id: 2, unit_id: 9, description: 'Water & sewer · Charleston Water System', amount: 96, period: 'July 2026',
    status: 'collected',
    allocation: [
      { space_name: 'Room A', tenant_name: 'Marcus Whitfield', share: 24 },
      { space_name: 'Room B', tenant_name: 'Keisha Boone', share: 24 },
      { space_name: 'Room C', tenant_name: 'Felix Ostrander', share: 24 },
      { space_name: 'Room D', tenant_name: 'Vacant', share: 24 },
    ],
  },
  {
    id: 3, unit_id: 9, description: 'Internet · Spectrum', amount: 80, period: 'August 2026',
    status: 'pending',
    allocation: [
      { space_name: 'Room A', tenant_name: 'Marcus Whitfield', share: 27 },
      { space_name: 'Room B', tenant_name: 'Keisha Boone', share: 27 },
      { space_name: 'Room C', tenant_name: 'Felix Ostrander', share: 26 },
    ],
  },
  // --- Ferncreek Rooming House (unit 10) ---
  {
    id: 4, unit_id: 10, description: 'Electricity · AEP Ohio', amount: 132, period: 'July 2026',
    status: 'allocated',
    allocation: [
      { space_name: 'Room 1', tenant_name: 'Renata Cho', share: 44 },
      { space_name: 'Room 2', tenant_name: 'Dwayne Pruitt', share: 44 },
      { space_name: 'Room 3', tenant_name: 'Vacant', share: 44 },
    ],
  },
  {
    id: 5, unit_id: 10, description: 'Water & sewer · City of Columbus', amount: 72, period: 'July 2026',
    status: 'pending',
    allocation: [
      { space_name: 'Room 1', tenant_name: 'Renata Cho', share: 24 },
      { space_name: 'Room 2', tenant_name: 'Dwayne Pruitt', share: 24 },
      { space_name: 'Room 3', tenant_name: 'Vacant', share: 24 },
    ],
  },
];

const DUMMY_WAITLIST: WaitlistEntry[] = [
  // Palmetto House (unit 9) — beyond the two formal applicants (Brianna Coles, Tomás Reyes)
  {
    id: 1, unit_id: 9, name: 'Devon Michaels', email: 'devon.michaels@email.com',
    desired_move_in: '2026-09-15', room_preference: 'Room D', status: 'Contacted', added_at: '2026-06-20',
  },
  {
    id: 2, unit_id: 9, name: 'Priya Nandakumar', email: 'priya.nandakumar@email.com',
    desired_move_in: '2026-10-01', room_preference: null, status: 'New', added_at: '2026-06-25',
  },
  {
    id: 3, unit_id: 9, name: 'Wesley Okonkwo', email: 'wesley.okonkwo@email.com',
    desired_move_in: '2026-09-01', room_preference: 'Room D', status: 'Touring', added_at: '2026-06-12',
  },
  // Ferncreek Rooming House (unit 10) — beyond the formal applicant (Sasha Alvarado)
  {
    id: 4, unit_id: 10, name: 'Latisha Freeman', email: 'latisha.freeman@email.com',
    desired_move_in: '2026-09-15', room_preference: 'Room 3', status: 'New', added_at: '2026-06-27',
  },
  {
    id: 5, unit_id: 10, name: 'Ian Castellanos', email: 'ian.castellanos@email.com',
    desired_move_in: '2026-09-07', room_preference: null, status: 'Contacted', added_at: '2026-06-18',
  },
];

const DUMMY_UPKEEP_TASKS: UpkeepTask[] = [
  // Palmetto House (unit 9) — kitchen-cleaning rotation among the current tenants
  {
    id: 1, unit_id: 9, area: 'Shared kitchen', task: 'Clean counters, stovetop & sink', frequency: 'Weekly',
    next_due: '2026-08-05', assigned_to: 'Marcus Whitfield',
  },
  {
    id: 2, unit_id: 9, area: 'Shared kitchen', task: 'Take out trash & recycling', frequency: 'Weekly',
    next_due: '2026-08-06', assigned_to: 'Keisha Boone',
  },
  {
    id: 3, unit_id: 9, area: 'Shared kitchen', task: 'Deep clean refrigerator', frequency: 'Monthly',
    next_due: '2026-08-15', assigned_to: 'Felix Ostrander',
  },
  {
    id: 4, unit_id: 9, area: 'Living room', task: 'Vacuum & tidy common seating', frequency: 'Biweekly',
    next_due: '2026-08-10', assigned_to: 'Cleaning pro',
  },
  {
    id: 7, unit_id: 9, area: 'Room D', task: 'Turnover clean — Room D', frequency: 'Monthly',
    next_due: '2026-08-08', assigned_to: 'Cleaning pro', space_id: 904,
  },
  // Ferncreek Rooming House (unit 10)
  {
    id: 5, unit_id: 10, area: 'Shared kitchen', task: 'Clean counters & appliances', frequency: 'Weekly',
    next_due: '2026-08-04', assigned_to: 'Renata Cho',
  },
  {
    id: 6, unit_id: 10, area: 'Shared kitchen', task: 'Take out trash & recycling', frequency: 'Weekly',
    next_due: '2026-08-08', assigned_to: 'Dwayne Pruitt',
  },
];

@Injectable({
  providedIn: 'root'
})
export class RoomOpsService {

  private http = inject(HttpClient);
  private demoData = inject(DemoDataService);
  private useDummyData = true;

  // Room operations have no backend yet — the sample expenses/waitlist/upkeep
  // belong only to the demo account; real accounts must see empty lists.
  private get showsDummyData(): boolean {
    return this.useDummyData && this.demoData.shouldServeTemplates();
  }

  async getSharedExpenses(unitId: number): Promise<SharedExpense[]> {
    if (this.useDummyData) {
      return this.showsDummyData ? DUMMY_SHARED_EXPENSES.filter(e => e.unit_id === unitId) : [];
    }
    return firstValueFrom(
      this.http.get<SharedExpense[]>(`${environment.api_url}api/v1/customer/rental-units/${unitId}/shared-expenses`)
    );
  }

  async getWaitlist(unitId: number): Promise<WaitlistEntry[]> {
    if (this.useDummyData) {
      return this.showsDummyData ? DUMMY_WAITLIST.filter(w => w.unit_id === unitId) : [];
    }
    return firstValueFrom(
      this.http.get<WaitlistEntry[]>(`${environment.api_url}api/v1/customer/rental-units/${unitId}/waitlist`)
    );
  }

  async getUpkeepTasks(unitId: number): Promise<UpkeepTask[]> {
    if (this.useDummyData) {
      return this.showsDummyData ? DUMMY_UPKEEP_TASKS.filter(t => t.unit_id === unitId) : [];
    }
    return firstValueFrom(
      this.http.get<UpkeepTask[]>(`${environment.api_url}api/v1/customer/rental-units/${unitId}/upkeep-tasks`)
    );
  }

  async addUpkeepTask(unitId: number, task: Omit<UpkeepTask, 'id'>): Promise<UpkeepTask> {
    if (this.useDummyData) {
      const nextId = Math.max(0, ...DUMMY_UPKEEP_TASKS.map(t => t.id)) + 1;
      const newTask: UpkeepTask = { ...task, id: nextId, unit_id: unitId };
      DUMMY_UPKEEP_TASKS.push(newTask);
      return newTask;
    }
    return firstValueFrom(
      this.http.post<UpkeepTask>(`${environment.api_url}api/v1/customer/rental-units/${unitId}/upkeep-tasks`, task)
    );
  }

  expenseStatusColor(status: SharedExpense['status']): string {
    switch (status) {
      case 'collected': return '#2E7D32';
      case 'allocated': return '#2196F3';
      case 'pending': return '#F9A825';
      default: return '#1E2125';
    }
  }

  waitlistStatusColor(status: WaitlistEntry['status']): string {
    switch (status) {
      case 'Applied': return '#2E7D32';
      case 'Touring': return '#2196F3';
      case 'Contacted': return '#F9A825';
      case 'New': return '#999';
      default: return '#1E2125';
    }
  }
}
